import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  AlertTriangle, 
  TrendingUp, 
  TrendingDown, 
  Settings, 
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  BarChart3,
  Target,
  FileText
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DeviationHandling = ({ user, onLogout }) => {
  const [deviations, setDeviations] = useState([]);
  const [dashboardData, setDashboardData] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedDeviation, setSelectedDeviation] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [analyzingClient, setAnalyzingClient] = useState(null);
  const [assetClasses, setAssetClasses] = useState([]);
  
  // Review dialog states
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewAction, setReviewAction] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');

  // Maker-checker approval queue states (checker role only)
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [approvalRationale, setApprovalRationale] = useState('');
  const [decidingApprovalId, setDecidingApprovalId] = useState(null);
  
  // Threshold dialog states
  const [thresholdDialogOpen, setThresholdDialogOpen] = useState(false);
  const [thresholdForm, setThresholdForm] = useState({
    client_id: '',
    threshold_type: 'percentage',
    asset_class: '',
    min_threshold: -10,
    max_threshold: 10
  });

  useEffect(() => {
    fetchData();
    fetchClients();
    fetchAssetClasses();
    if (user?.role === 'admin') {
      fetchPendingApprovals();
    }
  }, []);

  useEffect(() => {
    console.log('selectedDeviation changed:', selectedDeviation);
  }, [selectedDeviation]);

  const fetchData = async () => {
    try {
      const config = { withCredentials: true };

      const response = await axios.get(`${API}/deviations/dashboard`, config);
      setDashboardData(response.data);
      setDeviations(response.data.recent_deviations || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to fetch deviation data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssetClasses = async () => {
    try {
      const { data } = await axios.get(`${API}/securities/asset-classes`, { withCredentials: true });
      setAssetClasses(data.asset_classes || []);
    } catch (error) {
      console.error('Error fetching asset classes:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const config = { withCredentials: true };

      const response = await axios.get(`${API}/rm/users`, config);
      const allUsers = response.data || [];
      setClients(allUsers.filter(u => u.user_type === 'client'));
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const analyzeClientDeviations = async (clientId) => {
    setAnalyzingClient(clientId);
    try {
      const config = { withCredentials: true };

      await axios.post(`${API}/deviations/analyze/${clientId}`, {}, config);
      toast.success('Portfolio deviation analysis completed');
      
      // Refresh data
      await fetchData();
    } catch (error) {
      console.error('Error analyzing deviations:', error);
      toast.error(error.response?.data?.detail || 'Failed to analyze portfolio deviations');
    } finally {
      setAnalyzingClient(null);
    }
  };

  const fetchDeviationRecommendations = async (deviationId) => {
    try {
      const config = { withCredentials: true };

      const response = await axios.get(`${API}/deviations/recommendations/${deviationId}`, config);
      setRecommendations(response.data.recommendations || []);
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      toast.error('Failed to fetch recommendations');
    }
  };

  const submitReview = async () => {
    if (!reviewAction || !reviewNotes.trim()) {
      toast.error('Please select an action and provide review notes');
      return;
    }

    try {
      const config = { withCredentials: true };

      const { data } = await axios.post(`${API}/deviations/review`, {
        deviation_id: selectedDeviation.id,
        review_action: reviewAction,
        review_notes: reviewNotes
      }, config);

      toast.success(data.message || 'Review submitted successfully');
      setReviewDialogOpen(false);
      setReviewAction('');
      setReviewNotes('');

      // Refresh data
      await fetchData();
      if (user?.role === 'admin') {
        await fetchPendingApprovals();
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Failed to submit review');
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      const config = { withCredentials: true };
      const { data } = await axios.get(`${API}/approvals/pending`, config);
      setPendingApprovals(data || []);
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
    }
  };

  const decideApproval = async (approvalId, decision) => {
    if (!approvalRationale.trim()) {
      toast.error('Please provide a rationale for this decision');
      return;
    }
    try {
      const config = { withCredentials: true };
      const { data } = await axios.post(`${API}/approvals/${approvalId}/decide`, {
        decision,
        rationale: approvalRationale
      }, config);
      toast.success(data.message || 'Decision recorded');
      setApprovalRationale('');
      setDecidingApprovalId(null);
      await fetchPendingApprovals();
      await fetchData();
    } catch (error) {
      console.error('Error deciding approval:', error);
      toast.error(error.response?.data?.detail || 'Failed to record decision');
    }
  };

  const createThreshold = async () => {
    console.log('Creating threshold with data:', thresholdForm);
    
    // Validation
    if (!thresholdForm.client_id) {
      toast.error('Please select a client');
      return;
    }
    
    if (!thresholdForm.asset_class) {
      toast.error('Please select an asset class');
      return;
    }
    
    try {
      const config = { withCredentials: true };

      const response = await axios.post(`${API}/deviations/thresholds`, thresholdForm, config);
      console.log('Threshold creation response:', response.data);
      
      toast.success('Deviation threshold saved successfully');
      
      setThresholdDialogOpen(false);
      setThresholdForm({
        client_id: '',
        threshold_type: 'percentage',
        asset_class: '',
        min_threshold: -10,
        max_threshold: 10
      });
    } catch (error) {
      console.error('Error creating threshold:', error);
      toast.error(error.response?.data?.detail || 'Failed to save threshold');
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'low': return 'bg-[#fffdf0] text-ugmonk-darker';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'high': return 'bg-[#fffdf0] text-ugmonk-darker';
      case 'critical': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'under_review': return 'bg-[#fffdf0] text-ugmonk-darker';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'escalated': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'under_review': return <Eye className="w-4 h-4" />;
      case 'approved': return <CheckCircle className="w-4 h-4" />;
      case 'rejected': return <XCircle className="w-4 h-4" />;
      case 'escalated': return <AlertTriangle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a1a1a]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{fontFamily: "'PerfectNineties', serif"}}>
              Deviation Handling
            </h1>
            <p className="text-gray-600">Monitor and manage portfolio deviations with AI-powered analysis</p>
          </div>
          <div className="flex gap-3">
            <Dialog open={thresholdDialogOpen} onOpenChange={setThresholdDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="font-semibold px-4 py-2 rounded-md transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg"
                  style={{ backgroundColor: '#1a1a1a', color: 'white', border: 'none' }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#1a1a1a';
                    e.target.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#1a1a1a';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  <Settings className="w-4 h-4" />
                  Set Thresholds
                </Button>
              </DialogTrigger>
              <DialogContent 
                className="max-w-md bg-white border border-gray-200 shadow-lg"
                style={{ backgroundColor: 'white', color: '#111827' }}
              >
                <DialogHeader className="pb-4">
                  <DialogTitle 
                    className="text-xl font-semibold text-gray-900"
                    style={{ color: '#111827' }}
                  >
                    Set Deviation Thresholds
                  </DialogTitle>
                </DialogHeader>
                <div 
                  className="space-y-4 bg-white p-1"
                  style={{ backgroundColor: 'white', color: '#111827' }}
                >
                  <div>
                    <Label className="text-gray-900 font-medium mb-2 block" style={{ color: '#111827' }}>
                      Client
                    </Label>
                    <select
                      value={thresholdForm.client_id}
                      onChange={(e) => setThresholdForm({...thresholdForm, client_id: e.target.value})}
                      className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-[#1a1a1a] focus:outline-none bg-white"
                      style={{ 
                        backgroundColor: 'white !important', 
                        color: '#111827 !important',
                        fontSize: '14px',
                        fontWeight: '500',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                      }}
                    >
                      <option 
                        value="" 
                        style={{ color: '#6b7280 !important', backgroundColor: 'white !important' }}
                      >
                        Select Client
                      </option>
                      {clients.map(client => (
                        <option 
                          key={client.id} 
                          value={client.id} 
                          style={{ color: '#111827 !important', backgroundColor: 'white !important' }}
                        >
                          {client.full_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <Label className="text-gray-900 font-medium mb-2 block" style={{ color: '#111827' }}>
                      Asset Class
                    </Label>
                    <select
                      value={thresholdForm.asset_class}
                      onChange={(e) => setThresholdForm({...thresholdForm, asset_class: e.target.value})}
                      className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-[#1a1a1a] focus:outline-none bg-white"
                      style={{ 
                        backgroundColor: 'white !important', 
                        color: '#111827 !important',
                        fontSize: '14px',
                        fontWeight: '500',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                      }}
                    >
                      <option value="" style={{ color: '#6b7280', backgroundColor: 'white' }}>Select Asset Class</option>
                      {assetClasses.map(ac => (
                        <option key={ac} value={ac} style={{ color: '#111827', backgroundColor: 'white' }}>{ac}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-900 font-medium mb-2 block" style={{ color: '#111827' }}>
                        Min Threshold (%)
                      </Label>
                      <Input
                        type="number"
                        value={thresholdForm.min_threshold}
                        onChange={(e) => setThresholdForm({...thresholdForm, min_threshold: parseFloat(e.target.value)})}
                        placeholder="-10"
                        className="w-full p-3 border-2 border-gray-300 rounded-lg bg-white text-gray-900 focus:border-[#1a1a1a] focus:outline-none"
                        style={{ 
                          backgroundColor: 'white !important', 
                          color: '#111827 !important',
                          fontSize: '14px',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }}
                      />
                    </div>
                    <div>
                      <Label className="text-gray-900 font-medium mb-2 block" style={{ color: '#111827' }}>
                        Max Threshold (%)
                      </Label>
                      <Input
                        type="number"
                        value={thresholdForm.max_threshold}
                        onChange={(e) => setThresholdForm({...thresholdForm, max_threshold: parseFloat(e.target.value)})}
                        placeholder="10"
                        className="w-full p-3 border-2 border-gray-300 rounded-lg bg-white text-gray-900 focus:border-[#1a1a1a] focus:outline-none"
                        style={{ 
                          backgroundColor: 'white !important', 
                          color: '#111827 !important',
                          fontSize: '14px',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }}
                      />
                    </div>
                  </div>
                  
                  <Button 
                    onClick={createThreshold}
                    disabled={!thresholdForm.client_id}
                    className="w-full font-semibold px-4 py-3 rounded-md transition-all duration-200 shadow-md hover:shadow-lg"
                    style={{ 
                      backgroundColor: !thresholdForm.client_id ? '#9ca3af' : '#ffd87a', 
                      color: 'black',
                      cursor: !thresholdForm.client_id ? 'not-allowed' : 'pointer',
                      border: 'none',
                      fontSize: '14px'
                    }}
                    onMouseEnter={(e) => {
                      if (!e.target.disabled) {
                        e.target.style.backgroundColor = '#f5cc6a';
                        e.target.style.transform = 'translateY(-1px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!e.target.disabled) {
                        e.target.style.backgroundColor = '#ffd87a';
                        e.target.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    Save Threshold
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={`grid w-full ${user?.role === 'admin' ? 'grid-cols-5 max-w-3xl' : 'grid-cols-4 max-w-2xl'}`}>
            <TabsTrigger value="dashboard">
              <BarChart3 className="w-4 h-4 mr-2" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="analyze">
              <RefreshCw className="w-4 h-4 mr-2" />
              Analyze
            </TabsTrigger>
            <TabsTrigger value="deviations">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Deviations
            </TabsTrigger>
            <TabsTrigger value="review">
              <FileText className="w-4 h-4 mr-2" />
              Review
            </TabsTrigger>
            {user?.role === 'admin' && (
              <TabsTrigger value="approvals">
                <CheckCircle className="w-4 h-4 mr-2" />
                Approvals
                {pendingApprovals.length > 0 && (
                  <Badge className="ml-2 bg-red-100 text-red-800">{pendingApprovals.length}</Badge>
                )}
              </TabsTrigger>
            )}
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <Card className="border-gray-200 bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Deviations</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {dashboardData.summary?.total_deviations || 0}
                      </p>
                    </div>
                    <AlertTriangle className="w-8 h-8 text-ugmonk-dark" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-gray-200 bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Pending Review</p>
                      <p className="text-2xl font-bold text-yellow-600">
                        {dashboardData.summary?.pending_deviations || 0}
                      </p>
                    </div>
                    <Clock className="w-8 h-8 text-yellow-600" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-gray-200 bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Critical</p>
                      <p className="text-2xl font-bold text-red-600">
                        {dashboardData.summary?.critical_deviations || 0}
                      </p>
                    </div>
                    <TrendingDown className="w-8 h-8 text-red-600" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-gray-200 bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Low Risk</p>
                      <p className="text-2xl font-bold text-ugmonk-dark">
                        {dashboardData.summary?.severity_breakdown?.low || 0}
                      </p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-ugmonk-dark" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Deviations */}
            <Card className="border-gray-200 bg-white">
              <CardHeader>
                <CardTitle className="text-gray-900">Recent Deviations</CardTitle>
              </CardHeader>
              <CardContent>
                {deviations.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <AlertTriangle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>No deviations found</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {deviations.map((deviation) => (
                      <div key={deviation.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-[#1a1a1a] transition-colors">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Badge className={getSeverityColor(deviation.severity)}>
                              {deviation.severity}
                            </Badge>
                            <Badge className={getStatusColor(deviation.status)}>
                              <span className="flex items-center gap-1">
                                {getStatusIcon(deviation.status)}
                                {deviation.status}
                              </span>
                            </Badge>
                            <Badge className="bg-[#fffdf0] text-ugmonk-darker">
                              {deviation.client_name || 'Unknown Client'}
                            </Badge>
                          </div>
                          <p className="font-medium text-gray-900">{deviation.description}</p>
                          <p className="text-sm text-gray-600">
                            Deviation: {deviation.deviation_percentage.toFixed(2)}% 
                            ({deviation.current_value.toFixed(2)} vs {deviation.model_value.toFixed(2)})
                          </p>
                          <p className="text-xs text-gray-500">
                            Client: {deviation.client_name} ({deviation.client_email}) • {new Date(deviation.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Button 
                          onClick={async () => {
                            console.log('Setting deviation:', deviation);
                            setSelectedDeviation(deviation);
                            await fetchDeviationRecommendations(deviation.id);
                            setTimeout(() => setActiveTab('review'), 100);
                          }}
                          className="font-medium px-3 py-2 rounded-md border transition-colors duration-200 flex items-center gap-1"
                          style={{
                            backgroundColor: 'white',
                            color: '#1a1a1a',
                            borderColor: '#1a1a1a'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#f0fdfa';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'white';
                          }}
                        >
                          <Eye className="w-4 h-4" />
                          <span>Review</span>
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analyze Tab */}
          <TabsContent value="analyze" className="mt-6">
            <Card className="border-gray-200 bg-white">
              <CardHeader>
                <CardTitle className="text-gray-900">Analyze Client Portfolios</CardTitle>
                <p className="text-sm text-gray-600">
                  Select a client to analyze their portfolio for deviations using AI
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                    <div>
                      <Label className="text-gray-900 font-medium mb-2 block" style={{ color: '#111827' }}>
                        Select Client
                      </Label>
                      <select
                        value={selectedClient}
                        onChange={(e) => setSelectedClient(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg focus:border-[#1a1a1a] focus:outline-none"
                        style={{
                          backgroundColor: 'white',
                          color: '#111827',
                          fontSize: '14px',
                          fontWeight: '500',
                          height: '46px',
                          padding: '0 12px'
                        }}
                      >
                        <option value="" style={{ color: '#6b7280', backgroundColor: 'white' }}>
                          Choose a client to analyze
                        </option>
                        {clients.map(client => (
                          <option
                            key={client.id}
                            value={client.id}
                            style={{ color: '#111827', backgroundColor: 'white' }}
                          >
                            {client.full_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Button
                        onClick={() => analyzeClientDeviations(selectedClient)}
                        disabled={!selectedClient || analyzingClient === selectedClient}
                        className="w-full font-semibold px-4 rounded-md transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
                        style={{
                          height: '46px',
                          backgroundColor: (!selectedClient || analyzingClient === selectedClient) ? '#9ca3af' : '#ffd87a',
                          color: 'black',
                          cursor: (!selectedClient || analyzingClient === selectedClient) ? 'not-allowed' : 'pointer',
                          border: 'none'
                        }}
                        onMouseEnter={(e) => {
                          if (!e.target.disabled) {
                            e.target.style.backgroundColor = '#f5cc6a';
                            e.target.style.transform = 'translateY(-1px)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!e.target.disabled) {
                            e.target.style.backgroundColor = '#ffd87a';
                            e.target.style.transform = 'translateY(0)';
                          }
                        }}
                      >
                        {analyzingClient === selectedClient ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            <span>Analyzing...</span>
                          </>
                        ) : (
                          <>
                            <Target className="w-4 h-4" />
                            <span>Analyze Deviations</span>
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  <div className="bg-[#fffdf0] border border-ugmonk-border rounded-lg p-4">
                    <h4 className="font-medium text-ugmonk-darker mb-2">AI Analysis Features:</h4>
                    <ul className="text-sm text-ugmonk-darker space-y-1">
                      <li>• Asset allocation deviations from model portfolio</li>
                      <li>• Security concentration risk assessment</li>
                      <li>• Risk profile alignment analysis</li>
                      <li>• Severity scoring and recommendations</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Deviations Tab */}
          <TabsContent value="deviations" className="mt-6">
            <div className="space-y-6">
              {deviations.map((deviation) => (
                <Card key={deviation.id} className="border-gray-200 bg-white">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <Badge className={getSeverityColor(deviation.severity)}>
                            {deviation.severity}
                          </Badge>
                          <Badge className={getStatusColor(deviation.status)}>
                            <span className="flex items-center gap-1">
                              {getStatusIcon(deviation.status)}
                              {deviation.status}
                            </span>
                          </Badge>
                          <Badge className="bg-[#fffdf0] text-ugmonk-darker">
                            {deviation.client_name || 'Unknown Client'}
                          </Badge>
                          <span className="text-sm text-gray-500">
                            {deviation.deviation_type.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                        
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {deviation.description}
                        </h3>
                        
                        <div className="mb-3 p-3 bg-[#fffdf0] rounded-lg border border-ugmonk-border">
                          <p className="text-sm font-medium text-ugmonk-darker">
                            Client: {deviation.client_name}
                          </p>
                          <p className="text-sm text-ugmonk-dark">
                            Email: {deviation.client_email}
                          </p>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div>
                            <p className="text-sm text-gray-600">Current Value</p>
                            <p className="font-semibold text-gray-900">{deviation.current_value.toFixed(2)}%</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Model Value</p>
                            <p className="font-semibold text-gray-900">{deviation.model_value.toFixed(2)}%</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Deviation</p>
                            <p className={`font-semibold ${deviation.deviation_percentage >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {deviation.deviation_percentage >= 0 ? '+' : ''}{deviation.deviation_percentage.toFixed(2)}%
                            </p>
                          </div>
                        </div>
                        
                        <p className="text-sm text-gray-500">
                          Created: {new Date(deviation.created_at).toLocaleDateString()}
                          {deviation.reviewed_by && (
                            <span> • Reviewed by: {deviation.reviewed_by}</span>
                          )}
                        </p>
                      </div>
                      
                      <Button 
                        onClick={async () => {
                          console.log('Setting deviation for review:', deviation);
                          setSelectedDeviation(deviation);
                          await fetchDeviationRecommendations(deviation.id);
                          setTimeout(() => setActiveTab('review'), 100);
                        }}
                        className="font-medium px-3 py-2 rounded-md border transition-colors duration-200 flex items-center gap-1"
                        style={{
                          backgroundColor: 'white',
                          color: '#1a1a1a',
                          borderColor: '#1a1a1a'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#f0fdfa';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'white';
                        }}
                      >
                        <Eye className="w-4 h-4" />
                        <span>Review</span>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Review Tab */}
          <TabsContent value="review" className="mt-6">
            {console.log('Review tab - selectedDeviation:', selectedDeviation)}
            {selectedDeviation ? (
              <div className="space-y-6">
                <Card className="border-gray-200 bg-white">
                  <CardHeader>
                    <CardTitle className="text-gray-900 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-ugmonk-dark" />
                      Deviation Review
                    </CardTitle>
                    {selectedDeviation && (
                      <div className="mt-2 p-3 bg-[#fffdf0] rounded-lg border border-ugmonk-border">
                        <p className="text-sm font-medium text-ugmonk-darker">
                          Client: {selectedDeviation.client_name}
                        </p>
                        <p className="text-sm text-ugmonk-dark">
                          Email: {selectedDeviation.client_email}
                        </p>
                      </div>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-2">{selectedDeviation.description}</h4>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <p className="text-sm text-gray-600">Current</p>
                            <p className="text-lg font-bold text-gray-900">{selectedDeviation.current_value.toFixed(2)}%</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Model</p>
                            <p className="text-lg font-bold text-gray-900">{selectedDeviation.model_value.toFixed(2)}%</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Deviation</p>
                            <p className={`text-lg font-bold ${selectedDeviation.deviation_percentage >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {selectedDeviation.deviation_percentage >= 0 ? '+' : ''}{selectedDeviation.deviation_percentage.toFixed(2)}%
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Recommendations */}
                      {recommendations.length > 0 && (
                        <div className="space-y-3">
                          <h4 className="font-semibold text-gray-900">AI Recommendations</h4>
                          {recommendations.map((rec, idx) => (
                            <div key={idx} className="border border-gray-200 rounded-lg p-4">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge className="bg-[#fffdf0] text-ugmonk-darker">
                                  {rec.recommendation_type.replace('_', ' ').toUpperCase()}
                                </Badge>
                                <Badge className={`${
                                  rec.action === 'buy' ? 'bg-green-100 text-green-800' :
                                  rec.action === 'sell' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {rec.action.toUpperCase()}
                                </Badge>
                              </div>
                              <p className="font-medium text-gray-900">{rec.security_name || 'Portfolio Rebalancing'}</p>
                              <p className="text-sm text-gray-600 mt-1">{rec.rationale}</p>
                              {rec.recommended_amount && (
                                <p className="text-sm font-medium text-ugmonk-dark mt-1">
                                  Amount: ₹{rec.recommended_amount.toLocaleString('en-IN')}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Review Actions */}
                      <div className="border-t pt-4">
                        <h4 className="font-semibold text-gray-900 mb-3">Review Action</h4>
                        <div className="grid grid-cols-3 gap-2 mb-4">
                          {['accept', 'reject', 'escalate'].map(action => (
                            <Button
                              key={action}
                              onClick={() => setReviewAction(action)}
                              className="font-medium px-3 py-2 rounded-md border transition-colors duration-200"
                              style={{
                                backgroundColor: reviewAction === action ? '#1a1a1a' : 'white',
                                color: reviewAction === action ? 'white' : '#374151',
                                borderColor: reviewAction === action ? '#1a1a1a' : '#d1d5db'
                              }}
                            >
                              {action.charAt(0).toUpperCase() + action.slice(1)}
                            </Button>
                          ))}
                        </div>
                        {reviewAction === 'accept' && (
                          <p className="text-xs text-gray-500 -mt-2 mb-4">
                            Retaining this deviation requires sign-off from a checker before it is finalized.
                          </p>
                        )}

                        <div className="mb-4">
                          <Label className="text-gray-900 font-medium mb-2 block" style={{ color: '#111827' }}>
                            Review Notes
                          </Label>
                          <textarea
                            value={reviewNotes}
                            onChange={(e) => setReviewNotes(e.target.value)}
                            placeholder="Provide detailed notes for your review decision..."
                            className="w-full p-3 border border-gray-300 rounded-lg min-h-[100px] resize-none bg-white text-gray-900 placeholder-gray-500 focus:border-[#1a1a1a] focus:outline-none"
                            style={{ 
                              backgroundColor: 'white', 
                              color: '#111827',
                              '::placeholder': { color: '#6b7280' }
                            }}
                            required
                          />
                        </div>
                        
                        <Button 
                          onClick={submitReview}
                          disabled={!reviewAction || !reviewNotes.trim()}
                          className="w-full font-semibold px-4 py-3 rounded-md transition-all duration-200 shadow-md hover:shadow-lg"
                          style={{
                            backgroundColor: (!reviewAction || !reviewNotes.trim()) ? '#9ca3af' : '#ffd87a',
                            color: 'black',
                            cursor: (!reviewAction || !reviewNotes.trim()) ? 'not-allowed' : 'pointer',
                            border: 'none'
                          }}
                          onMouseEnter={(e) => {
                            if (!e.target.disabled) {
                              e.target.style.backgroundColor = '#f5cc6a';
                              e.target.style.transform = 'translateY(-1px)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!e.target.disabled) {
                              e.target.style.backgroundColor = '#ffd87a';
                              e.target.style.transform = 'translateY(0)';
                            }
                          }}
                        >
                          Submit Review
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card className="border-gray-200 bg-white">
                <CardContent className="text-center py-12">
                  <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Deviation Selected</h3>
                  <p className="text-gray-600 mb-4">Select a deviation from the Dashboard or Deviations tab to review</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Approvals Tab (checker role - maker-checker sign-off queue) */}
          {user?.role === 'admin' && (
            <TabsContent value="approvals" className="mt-6">
              <Card className="border-gray-200 bg-white">
                <CardHeader>
                  <CardTitle className="text-gray-900 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-ugmonk-dark" />
                    Pending Approvals
                  </CardTitle>
                  <p className="text-sm text-gray-600">
                    RM discretion actions (risk profile overrides, retained deviations) require independent sign-off before they take effect.
                  </p>
                </CardHeader>
                <CardContent>
                  {pendingApprovals.length === 0 ? (
                    <div className="text-center py-12">
                      <CheckCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No Pending Approvals</h3>
                      <p className="text-gray-600">All maker-checker requests have been decided.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {pendingApprovals.map((approval) => (
                        <div key={approval.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className="bg-[#fffdf0] text-ugmonk-darker">
                              {approval.entity_type === 'risk_override' ? 'Risk Profile Override' : 'Deviation Retained'}
                            </Badge>
                            <Badge className="bg-yellow-100 text-yellow-800">
                              <Clock className="w-3 h-3 mr-1" />
                              Pending
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-900">
                            Requested by <span className="font-medium">{approval.requested_by_name}</span> on{' '}
                            {new Date(approval.requested_at).toLocaleString()}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">Rationale: {approval.rationale}</p>

                          {decidingApprovalId === approval.id ? (
                            <div className="mt-3 space-y-2">
                              <Textarea
                                value={approvalRationale}
                                onChange={(e) => setApprovalRationale(e.target.value)}
                                placeholder="Decision rationale (required)..."
                                className="min-h-[80px]"
                              />
                              <div className="flex gap-2">
                                <Button
                                  onClick={() => decideApproval(approval.id, 'approve')}
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" /> Approve
                                </Button>
                                <Button
                                  onClick={() => decideApproval(approval.id, 'reject')}
                                  className="bg-red-600 hover:bg-red-700 text-white"
                                >
                                  <XCircle className="w-4 h-4 mr-1" /> Reject
                                </Button>
                                <Button
                                  onClick={() => { setDecidingApprovalId(null); setApprovalRationale(''); }}
                                  variant="outline"
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <Button
                              className="mt-3"
                              onClick={() => setDecidingApprovalId(approval.id)}
                            >
                              Review Decision
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Layout>
  );
};

export default DeviationHandling;
